import { Box, Flex, IconButton, Text } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';

import { DEFAULT_DATE_FORMAT, isDefined } from '@enterprise/core';
import { DateFormat, TrashCan } from '@enterprise/ui';

import { renderRichBody } from '@feat/notifications/utilities/render-rich-body';

import type { NotificationRowProps } from './types/notification-row.props';

import { useNotificationRowConfiguration } from './hooks/notification-row-configuration.hook';

// Slide-in for a just-arrived row (driven by `isFresh`); ported from the prototype's `item-in`.
const rowSlideIn = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const NotificationRow = ({ isFresh, notification, onDelete, onOpen }: NotificationRowProps) => {
  const { handleDelete, handleOpen, Icon, isRunning, isUnread, presentation } = useNotificationRowConfiguration(
    notification,
    onOpen,
    onDelete
  );

  return (
    <Flex
      _hover={{
        '[data-row-delete]': { opacity: 1 },
        '[data-row-dot]': { opacity: 0 },
        bg: 'gray.50',
      }}
      align="stretch"
      animation={isFresh ? `${rowSlideIn} 0.34s cubic-bezier(0.2, 0.9, 0.3, 1) both` : undefined}
      bg={isUnread ? `color-mix(in srgb, ${presentation.color} 8%, #fff)` : 'white'}
      borderBottom="1px solid"
      borderColor="gray.100"
      borderLeft={isUnread ? `3px solid ${presentation.color}` : '3px solid transparent'}
      data-fresh={isFresh ?? false}
      data-unread={isUnread}
      position="relative"
      role="listitem"
      transition="background 0.15s">
      <Box
        aria-label="Open notification"
        as="button"
        display="flex"
        flex={1}
        gap={3}
        onClick={handleOpen}
        px="14px"
        py="11px"
        textAlign="left"
        type="button">
        <Flex
          align="center"
          color={presentation.color}
          data-running={isRunning}
          flexShrink={0}
          fontSize="larger">
          <Icon
            aria-hidden
            spin={isRunning}
          />
        </Flex>
        <Box
          flex={1}
          minW={0}>
          <Text
            fontWeight={isUnread ? 'medium' : 'normal'}
            lineHeight="1.35"
            mb={0.5}
            noOfLines={3}>
            {renderRichBody(notification.body)}
          </Text>
          <Flex
            align="center"
            color="gray.500"
            fontSize="sm"
            gap={1.5}>
            {isDefined(notification.sourceCode) && (
              <Text
                as="span"
                color="brand.500"
                fontFamily="mono"
                fontWeight="bold">
                {notification.sourceCode}
              </Text>
            )}
            {isDefined(notification.sourceCode) && <Text as="span">·</Text>}
            <DateFormat
              format={{
                format: `${DEFAULT_DATE_FORMAT} @ h:mm a`,
                isRelative: true,
              }}
              value={notification.updatedDate}
            />
          </Flex>
        </Box>
      </Box>
      <Flex
        align="center"
        flexShrink={0}
        justify="center"
        position="relative"
        px={2}
        w="36px">
        {isUnread && (
          <Box
            bg={presentation.color}
            borderRadius="full"
            boxSize="10px"
            data-row-dot
            transition="opacity 0.12s"
          />
        )}
        <IconButton
          _hover={{ bg: 'red.100', color: 'red.500' }}
          aria-label="Delete notification"
          color="gray.400"
          data-row-delete
          icon={<TrashCan />}
          onClick={handleDelete}
          opacity={0}
          position="absolute"
          size="sm"
          transition="opacity 0.12s"
          variant="ghost"
        />
      </Flex>
    </Flex>
  );
};
